import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { SupplierUser } from '@/types/suppliers'
import { sendMagicLink as apiSendMagicLink, verifyCode as apiVerifyCode, getSession, destroySession } from '@/api/portalSessions'
import { useAsyncAction } from '@/composables/useAsyncAction'

export const usePortalAuthStore = defineStore('portalAuth', () => {
  const supplierUser = ref<SupplierUser | null>(null)
  const authenticated = ref(false)
  const initialized = ref(false)

  const { loading, error, execute } = useAsyncAction()

  const isAuthenticated = computed(() => authenticated.value)

  async function sendMagicLink(email: string) {
    return await execute(async () => {
      const { data } = await apiSendMagicLink(email)
      return data
    })
  }

  async function verifyCode(code: string, email: string) {
    return await execute(async () => {
      await apiVerifyCode(code, email)
      await fetchCurrentUser()
    })
  }

  async function fetchCurrentUser() {
    await execute(async () => {
      const { data } = await getSession()
      supplierUser.value = data
      authenticated.value = true
    })
  }

  async function checkAuth() {
    try {
      await fetchCurrentUser()
    } catch {
      supplierUser.value = null
      authenticated.value = false
    } finally {
      initialized.value = true
    }
  }

  async function logout() {
    try {
      await destroySession()
    } catch {
      // silently continue - clear local state regardless
    } finally {
      supplierUser.value = null
      authenticated.value = false
    }
  }

  return {
    supplierUser,
    loading,
    error,
    initialized,
    isAuthenticated,
    sendMagicLink,
    verifyCode,
    fetchCurrentUser,
    checkAuth,
    logout,
  }
})
